import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import useTokenRefresh from '../hooks/useTokenRefresh';
import { toast } from 'react-toastify';
import { ModalView } from '../provider/modal.provider';
import "./token-refresh-modal.scss";

const TokenRefreshModal = () => {
  const { t } = useTranslation();
  const { showRefreshWarning, refreshToken, dismissAndLogout, tokenExpireTime } = useTokenRefresh();
  const [countdown, setCountdown] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
      const html = document.getElementsByTagName("html")[0];
      html.setAttribute("data-wf-page", "68603f7a056ff6f78f58cb16");
      html.setAttribute("data-wf-site", "68603f7a056ff6f78f58cab1");
      window.Webflow && window.Webflow.destroy();
      window.Webflow && window.Webflow.ready();
      window.Webflow && window.Webflow.require("ix2").init();
      document.dispatchEvent(new Event("readystatechange"));
    }, []);

  // Calculate countdown to expiration
  useEffect(() => {
    if (!showRefreshWarning || !tokenExpireTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const expireTime = new Date(tokenExpireTime);
      const timeLeft = Math.max(0, Math.floor((expireTime - now) / 1000));
      setCountdown(timeLeft);

      if (timeLeft === 0) {
        clearInterval(interval);
        dismissAndLogout();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showRefreshWarning, tokenExpireTime, dismissAndLogout]);

  const handleExtendSession = async () => {
    setIsRefreshing(true);
    const success = await refreshToken();
    setIsRefreshing(false);
    
    if (!success) {
      toast.error(t('errorInGeneratingToken') || 'Error in generating token');
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!showRefreshWarning) return null;

  return (
    <ModalView
      size="lg"
      fader={'show'}
      title={t('sessionExpiringWarning') || 'Session Expiring Soon'}
      onCloseEvent={handleExtendSession}
      footer={
        <>        
          <div className="modal-footer">
              <div className="primary-button-wrapper">
                 <a
                   href="#"
                   onClick={() => dismissAndLogout()}
                   disabled={isRefreshing}
                   data-w-id="3af1c84f-ff74-c014-cb3b-40446b73b1f6"
                   className="primary-button w-inline-block"
                 >
                   <div className="primary-button-border-wrap">
                     <div className="primary-button-wrap">
                       <div className="primary-button-text">
                         {t('logout') || 'Logout'}
                       </div>
                       <div className="primary-button-hover-text">
                         {t('logout') || 'Logout'}
                       </div>
                       <div className="primary-button-hover-bg"></div>
                     </div>
                   </div>
                   <div className="line-wrap">
                     <div className="primary-line-wrap _1">
                       <div className="line _1"></div>
                       <div className="line _2"></div>
                     </div>
                     <div className="primary-line-wrap _2">
                       <div className="line _3"></div>
                       <div className="line _4"></div>
                     </div>
                     <div className="primary-line-wrap _3">
                       <div className="line _5"></div>
                       <div className="line _6"></div>
                     </div>
                     <div className="primary-line-wrap _4">
                       <div className="line _7"></div>
                       <div className="line _8"></div>
                     </div>
                   </div>
                 </a>
              </div>            
          </div>

          <div className="modal-footer">
              <div className="primary-button-wrapper">
                 <a
                   href="#"
                   onClick={handleExtendSession}
                   disabled={isRefreshing}
                   data-w-id="3af1c84f-ff74-c014-cb3b-40446b73b1f6"
                   className="primary-button w-inline-block"
                 >
                   <div className="primary-button-border-wrap">
                     <div className="primary-button-wrap">
                       <div className="primary-button-text">
                         {isRefreshing ? (
                               <>
                                 <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                 {t('extending') || 'Extending...'}
                               </>
                             ) : (
                               t('extendSession') || 'Extend Session'
                             )}
                       </div>
                       <div className="primary-button-hover-text">
                         {isRefreshing ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('extending') || 'Extending...'}
                              </>
                            ) : (
                              t('extendSession') || 'Extend Session'
                            )}
                       </div>
                       <div className="primary-button-hover-bg"></div>
                     </div>
                   </div>
                   <div className="line-wrap">
                     <div className="primary-line-wrap _1">
                       <div className="line _1"></div>
                       <div className="line _2"></div>
                     </div>
                     <div className="primary-line-wrap _2">
                       <div className="line _3"></div>
                       <div className="line _4"></div>
                     </div>
                     <div className="primary-line-wrap _3">
                       <div className="line _5"></div>
                       <div className="line _6"></div>
                     </div>
                     <div className="primary-line-wrap _4">
                       <div className="line _7"></div>
                       <div className="line _8"></div>
                     </div>
                   </div>
                 </a>
              </div>            
        </div>
        </>
      }
    >
      <Box className="session-expiry-row" sx={{marginRight: '0 !important', marginLeft: '0 !important'}}>
        <Box className="session-expiry-col">
          <Typography variant="h6" className="mb-3">
            {t('sessionWillExpireMessage') || 'Your session will expire in:'} 
          </Typography>
          <div className="modal-footer">
            <div className="primary-button-wrapper">
               <a className="primary-button w-inline-block no-hover-button">
                 <div className="primary-button-border-wrap" style={{borderRadius:"1.5rem"}}>
                   <div className="primary-button-wrap" style={{borderRadius:"1.5rem"}}>
                     <div className="">
                         {formatTime(countdown)} {countdown <= 60 ? `${t('seconds') || 'Sec'}` : `${t('minutes') || 'Min'}`}
                     </div>
                   </div>
                 </div>
               </a>
            </div>            
           </div>
          <Typography variant="body2" className="session-expiry-note">
            {t('clickExtendToContinue') || 'Click "Extend Session" to continue working, or you will be automatically logged out.'}
          </Typography>
        </Box>
      </Box>
    </ModalView>
  );
};

export default TokenRefreshModal;
